import * as DocumentPicker from "expo-document-picker";
import Text from "@/components/shared/AppText";
import { notify } from "@/components/shared/AppToast";
import { t } from "@/localization";

import React, { useState } from "react";
import { Platform } from "react-native";
import OnboardingButton from "@/components/shared/OnboardingButton";
import tw from "twrnc";

import { uploadData } from "aws-amplify/storage";

const MIME_BY_EXTENSION = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  pdf: "application/pdf",
  txt: "text/plain",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

const inferContentType = ({ mimeType, fileType, blobType, name }) => {
  const explicitType = [mimeType, fileType, blobType].find(
    (value) => typeof value === "string" && value.trim().length > 0
  );
  if (explicitType) return explicitType;

  const extension = `${name || ""}`.split(".").pop()?.toLowerCase();
  if (extension && MIME_BY_EXTENSION[extension]) {
    return MIME_BY_EXTENSION[extension];
  }

  return "application/octet-stream";
};

const safeIosObjectName = (name) => {
  const trimmed = `${name || ""}`.trim();
  const fallback = `upload-${Date.now()}`;
  return (trimmed || fallback)
    .replace(/\s+/g, "-")
    .replace(/[^\w.-]/g, "-")
    .replace(/-+/g, "-");
};

// Pick a file and upload it to S3. Returns { pick, loading }; pick() resolves
// after onComplete ran (or the picker was cancelled).
export const useFileUpload = ({
  type = "image/*",
  path,
  bucket,
  onStart,
  onComplete,
  onCancel,
}) => {
  const [loading, setloading] = useState(false);

  // No cancel on web: https://github.com/expo/expo/issues/8477
  const pick = async () => {
    try {
      if (onStart) {
        onStart();
      }
      const r = await DocumentPicker.getDocumentAsync({
        type,
        multiple: false,
      });

      const { assets, canceled } = r;

      setloading(true);

      if (canceled) {
        setloading(false);
        if (onCancel) onCancel();
        return;
      }

      const { name, file, uri, mimeType } = assets?.[0];
      const sourceName = `${name || "upload"}`;
      const objectName =
        Platform.OS === "ios" ? safeIosObjectName(sourceName) : sourceName;

      let content;

      if (Platform.OS === "web") {
        content = file;
      } else {
        const finaluri = Platform.OS === "ios" ? uri : `file://${uri}`;
        const response = await fetch(finaluri);
        content = await response.blob();
      }

      const contentType = inferContentType({
        mimeType,
        fileType: file?.type,
        blobType: content?.type,
        name: sourceName,
      });

      const uploadTask = uploadData({
        path: `${path}/${objectName}`,
        data: content,

        options: {
          contentType,
          bucket: {
            bucketName: bucket,
            region: "us-east-1",
          },
          accessLevel: "public-read",
        },
      });

      await uploadTask.result;

      if (onComplete) {
        await onComplete({
          name,
          file,
          uri,
          type,
          contentType,
          url: `https://${bucket}.s3.amazonaws.com/${path}/${encodeURIComponent(
            objectName
          )}`,
        });
      }
      setloading(false);
    } catch (err) {
      setloading(false);
      if (onCancel) onCancel();
      console.error(err);

      notify.error(t("Upload failed"), String(err?.message || err));
    }
  };

  return { pick, loading };
};

const UploadFile = ({
  type = "image/*",
  path,
  bucket,
  disclaimer,
  onStart,
  onComplete,
  onCancel,
  bordered,
  dark,
  buttonTitle = t("Upload File"),
  renderButton,
}) => {
  const { pick, loading } = useFileUpload({
    type,
    path,
    bucket,
    onStart,
    onCancel,
    onComplete: async (result) => {
      notify.success(t("File uploaded"));
      if (onComplete) await onComplete(result);
    },
  });

  return (
    <>
      {renderButton ? (
        renderButton({ onPress: pick, loading })
      ) : (
        <OnboardingButton
          onPress={pick}
          dark={dark}
          bordered={bordered}
          icon="document-outline"
          loading={loading}
          text={buttonTitle}
        />
      )}
      {disclaimer && <Text style={tw`my-5 text-xs`}>{disclaimer}</Text>}
    </>
  );
};

export default UploadFile;
